// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * LE MOTEUR DU TABLEAU — module PUR (L0, 10/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tout ce que `<Tableau>` (Tableau.tsx) décide SANS toucher au DOM vit ici, et
 * c'est ce que `backend/tests/front-tableau-socle.test.js` rejoue : les niveaux,
 * l'ordre et le masquage des colonnes, le cumul des colonnes épinglées, la
 * fenêtre de virtualisation avec rangées dépliables, les filtres par colonne,
 * les actions par lot et leur bilan.
 *
 * Aucun import : ce module est recopié dans les trois applications par
 * `shared/sync.sh` et compilé seul par le lanceur de tests du backend.
 *
 * ── Les règles que ce module FIGE (PROMPT-TABLEAUX §1) ─────────────────────
 *
 *  · Trois niveaux — `simple`, `standard`, `avance` — qui ACTIVENT des fonctions.
 *    Un tableau sans niveau garde le comportement historique de `TableauTriable`
 *    (tri, ordre, largeur), pour que les 25 appels existants ne changent pas
 *    d'un pixel tant que leur lot ne les configure pas (L3 à L6).
 *  · Les préférences ne portent que les ÉCARTS au défaut : une préférence vide
 *    n'est jamais écrite, et une colonne que le code ne connaît plus est
 *    ignorée à la relecture (une préférence périmée ne fait jamais écran blanc).
 *  · Les colonnes OBLIGATOIRES (paramétrage du laboratoire) ne se masquent pas.
 *  · Une action par lot traite les lignes UNE PAR UNE — une entrée de journal
 *    par ligne — et il n'existe PAS de suppression par lot : le type l'interdit
 *    et l'exécution le refuse.
 *  · La largeur d'une colonne est bornée entre 56 et 420 px, en lecture comme
 *    en écriture.
 */

/* ───────────────────────────── Niveaux ───────────────────────────────────── */

export type Niveau = 'simple' | 'standard' | 'avance';
export type Densite = 'normale' | 'compacte';
export type TypeColonne = 'texte' | 'nombre' | 'date' | 'liste';

export interface Capacites {
  tri: boolean;
  /** Recherche globale (toutes les colonnes triables). */
  recherche: boolean;
  /** Filtres PAR colonne (texte, liste, plage de dates, plage numérique). */
  filtres: boolean;
  masquer: boolean;
  redimensionner: boolean;
  pagination: boolean;
  export: boolean;
  reordonner: boolean;
  epingler: boolean;
  /** Sélection de lignes et actions par lot. */
  lot: boolean;
}

/**
 * Ce que chaque niveau active (AUDIT-TABLES §5).
 *
 * ⚠️ `undefined` = HÉRITAGE : exactement ce que `TableauTriable` faisait avant
 * L0 (tri, réordonner, régler la largeur, paginer si `parPage`). Aucune barre
 * d'outils, aucun menu : l'écran ne change pas avant son propre lot.
 */
export function capacites(niveau: Niveau | undefined): Capacites {
  switch (niveau) {
    case 'simple':
      return { tri: true, recherche: true, filtres: false, masquer: false, redimensionner: false, pagination: false, export: false, reordonner: false, epingler: false, lot: false };
    case 'standard':
      return { tri: true, recherche: true, filtres: true, masquer: true, redimensionner: true, pagination: true, export: true, reordonner: true, epingler: false, lot: false };
    case 'avance':
      return { tri: true, recherche: true, filtres: true, masquer: true, redimensionner: true, pagination: true, export: true, reordonner: true, epingler: true, lot: true };
    default:
      return { tri: true, recherche: false, filtres: false, masquer: false, redimensionner: true, pagination: true, export: false, reordonner: true, epingler: false, lot: false };
  }
}

/* ───────────────────────────── Préférences ───────────────────────────────── */

export interface PrefTableau {
  ordre: string[];
  masquees: string[];
  epinglees: string[];
  largeurs: Record<string, number>;
  /** Le tri courant, retenu avec le reste (L1). `null` = l'ordre métier d'origine. */
  tri?: { cle: string; sens: 1 | -1 } | null;
}

export const PREF_VIDE: PrefTableau = { ordre: [], masquees: [], epinglees: [], largeurs: {}, tri: null };

/** En deçà, une colonne n'affiche plus rien d'utile — et l'on ne sait plus la rattraper à la souris. */
export const LARGEUR_MIN = 56;
/** Au-delà, une seule colonne pousse toutes les autres hors de l'écran. */
export const LARGEUR_MAX = 420;

export const bornerLargeur = (px: number): number => Math.max(LARGEUR_MIN, Math.min(LARGEUR_MAX, Math.round(px)));

const listeDeCles = (v: unknown): string[] => (Array.isArray(v)
  ? Array.from(new Set(v.filter((x) => typeof x === 'string' && x) as string[]))
  : []);

/** Lit une préférence enregistrée, avec tolérance : n'importe quoi → préférence vide, largeurs BORNÉES. */
export function lirePref(brut: unknown): PrefTableau {
  try {
    const o = typeof brut === 'string' ? JSON.parse(brut) : brut;
    if (!o || typeof o !== 'object') return PREF_VIDE;
    const p = o as Partial<PrefTableau>;
    const largeurs: Record<string, number> = {};
    if (p.largeurs && typeof p.largeurs === 'object') {
      for (const [k, v] of Object.entries(p.largeurs)) {
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) largeurs[k] = bornerLargeur(v);
      }
    }
    const t = p.tri as { cle?: unknown; sens?: unknown } | null | undefined;
    const tri = t && typeof t === 'object' && typeof t.cle === 'string' && t.cle && (t.sens === 1 || t.sens === -1)
      ? { cle: t.cle, sens: t.sens as 1 | -1 } : null;
    return { ordre: listeDeCles(p.ordre), masquees: listeDeCles(p.masquees), epinglees: listeDeCles(p.epinglees), largeurs, tri };
  } catch {
    return PREF_VIDE;
  }
}

/** Ne garde que les ÉCARTS au défaut ; `null` quand il n'y en a aucun (= rien à écrire). */
export function ecartsPref(defauts: readonly string[], pref: PrefTableau): Partial<PrefTableau> | null {
  const out: Partial<PrefTableau> = {};
  const ordre = colonnesOrdonnees(defauts, pref);
  if (ordre.some((k, i) => k !== defauts[i])) out.ordre = ordre;
  const masquees = pref.masquees.filter((k) => defauts.includes(k));
  if (masquees.length) out.masquees = masquees;
  const epinglees = pref.epinglees.filter((k) => defauts.includes(k));
  if (epinglees.length) out.epinglees = epinglees;
  const largeurs = Object.fromEntries(Object.entries(pref.largeurs).filter(([k]) => defauts.includes(k)));
  if (Object.keys(largeurs).length) out.largeurs = largeurs;
  if (pref.tri && defauts.includes(pref.tri.cle)) out.tri = pref.tri;
  return Object.keys(out).length ? out : null;
}

/**
 * TOUTES les colonnes, masquées comprises, dans l'ordre choisi.
 * Les trois garde-fous de `lib/colonnes-tableau.ts` (03/10/2026), repris ici :
 * clé inconnue ignorée ; colonne nouvelle à sa place d'origine, après sa
 * voisine ; préférence illisible = ordre d'origine.
 */
export function colonnesOrdonnees(defauts: readonly string[], pref: Pick<PrefTableau, 'ordre'>): string[] {
  const connues = new Set(defauts);
  const res = pref.ordre.filter((k, i) => connues.has(k) && pref.ordre.indexOf(k) === i);
  defauts.forEach((k, i) => {
    if (res.includes(k)) return;
    let apres = -1;
    for (let j = i - 1; j >= 0; j -= 1) { const pos = res.indexOf(defauts[j]!); if (pos >= 0) { apres = pos; break; } }
    res.splice(apres + 1, 0, k);
  });
  return res;
}

/**
 * Les colonnes AFFICHÉES, dans l'ordre : les épinglées D'ABORD (dans leur
 * ordre), puis les autres. Une colonne obligatoire ne peut pas être masquée —
 * si une préférence antérieure la masquait, elle réapparaît.
 */
export function colonnesVisibles(
  defauts: readonly string[],
  pref: PrefTableau,
  obligatoires: readonly string[] = [],
  /** Épinglées PAR LE CODE (la colonne d'identifiant d'un gros registre) : toujours en tête, avant celles de la personne. */
  epingleesFixes: readonly string[] = [],
): string[] {
  const ordre = colonnesOrdonnees(defauts, pref);
  const masquees = new Set(pref.masquees.filter((k) => !obligatoires.includes(k)));
  const visibles = ordre.filter((k) => !masquees.has(k));
  const fixes = epingleesFixes.filter((k) => visibles.includes(k));
  const epinglees = [...fixes, ...visibles.filter((k) => pref.epinglees.includes(k) && !fixes.includes(k))];
  return [...epinglees, ...visibles.filter((k) => !epinglees.includes(k))];
}

/** Monte (-1) ou descend (+1) une colonne d'un cran ; sans effet aux extrémités. */
export function deplacerColonne(defauts: readonly string[], pref: PrefTableau, cle: string, sens: -1 | 1): PrefTableau {
  const ordre = colonnesOrdonnees(defauts, pref);
  const i = ordre.indexOf(cle); const j = i + sens;
  if (i < 0 || j < 0 || j >= ordre.length) return { ...pref, ordre };
  [ordre[i], ordre[j]] = [ordre[j]!, ordre[i]!];
  return { ...pref, ordre };
}

/** Dépose la colonne `portee` à la place de `cible` (glisser-déposer d'en-tête). */
export function deposerColonne(defauts: readonly string[], pref: PrefTableau, portee: string, cible: string): PrefTableau {
  const ordre = colonnesOrdonnees(defauts, pref);
  const de = ordre.indexOf(portee); const a = ordre.indexOf(cible);
  if (de < 0 || a < 0 || de === a) return { ...pref, ordre };
  ordre.splice(a, 0, ...ordre.splice(de, 1));
  return { ...pref, ordre };
}

/** Affiche une colonne masquée, ou masque une colonne affichée — jamais une obligatoire. */
export function basculerColonne(defauts: readonly string[], pref: PrefTableau, cle: string, obligatoires: readonly string[] = []): PrefTableau {
  if (!defauts.includes(cle) || obligatoires.includes(cle)) return pref;
  const masquees = pref.masquees.includes(cle) ? pref.masquees.filter((k) => k !== cle) : [...pref.masquees, cle];
  return { ...pref, masquees };
}

/** Épingle à gauche, ou désépingle. */
export function basculerEpingle(defauts: readonly string[], pref: PrefTableau, cle: string): PrefTableau {
  if (!defauts.includes(cle)) return pref;
  const epinglees = pref.epinglees.includes(cle) ? pref.epinglees.filter((k) => k !== cle) : [...pref.epinglees, cle];
  return { ...pref, epinglees };
}

/** La préférence diffère-t-elle du défaut ? (pour proposer « Rétablir ») */
export function estPersonnalisee(defauts: readonly string[], pref: PrefTableau): boolean {
  return ecartsPref(defauts, pref) !== null;
}

/* ───────────────────────────── Mise en page ──────────────────────────────── */

/**
 * `colSpan` de la rangée DÉPLIÉE : les colonnes VISIBLES, plus la case de
 * sélection et la colonne d'actions quand elles sont rendues.
 *
 * ⚠️ Sur les colonnes visibles et non sur toutes : une colonne masquée ferait
 * déborder le détail d'une cellule, et le tableau se décalerait sans qu'on
 * sache pourquoi.
 */
export function colSpanDetail(nbVisibles: number, o: { selection?: boolean; actions?: boolean } = {}): number {
  return Math.max(1, nbVisibles + (o.selection ? 1 : 0) + (o.actions ? 1 : 0));
}

/**
 * Décalage (`insetInlineStart`) de chaque colonne épinglée : le CUMUL des
 * largeurs des épinglées qui la précèdent, plus la case de sélection si elle
 * est rendue. Une largeur inconnue (colonne jamais mesurée) prend `defaut`.
 *
 * Propriété LOGIQUE et non `left` : la plateforme est trilingue, et en RTL la
 * colonne doit s'épingler du côté où commence la lecture (É-11).
 */
export function decalagesEpingles(
  visibles: readonly string[],
  epinglees: readonly string[],
  largeurs: Readonly<Record<string, number>>,
  defaut: number,
  largeurSelection = 0,
): Record<string, number> {
  const out: Record<string, number> = {};
  let cumul = largeurSelection;
  for (const k of visibles) {
    if (!epinglees.includes(k)) break; // les épinglées sont toujours en tête
    out[k] = cumul;
    cumul += largeurs[k] ?? defaut;
  }
  return out;
}

/* ───────────────────────────── Virtualisation ────────────────────────────── */

/** Au-delà, les lignes hors écran ne sont plus rendues (É-14). */
export const SEUIL_VIRTUALISATION = 200;

/** Un tableau paginé découpe déjà ; on ne virtualise que la liste entière. */
export function doitVirtualiser(nbLignes: number, parPage: number | undefined, seuil = SEUIL_VIRTUALISATION): boolean {
  return !parPage && nbLignes > seuil;
}

/** Hauteur d'un ÉLÉMENT virtuel : la ligne, plus son détail déplié s'il existe. */
export function hauteurItem(ligne: number, detail: number | null | undefined): number {
  return ligne + (detail ?? 0);
}

/** Ce qu'il faut à `mesurerLigneEtDetail` : une rangée et, peut-être, la rangée de détail qui la suit. */
export interface RangeeMesurable {
  offsetHeight: number;
  nextElementSibling: { offsetHeight: number; getAttribute(nom: string): string | null } | null;
}

/**
 * Mesure une rangée ET sa rangée de détail (marquée `data-detail`) : le
 * virtualiseur ne connaît qu'un élément par index, et une rangée dépliée en
 * rend deux. Sans cette somme, chaque dépli décalerait toutes les lignes
 * suivantes de sa hauteur.
 */
export function mesurerLigneEtDetail(el: RangeeMesurable): number {
  const suivant = el.nextElementSibling;
  const detail = suivant && suivant.getAttribute('data-detail') !== null ? suivant.offsetHeight : 0;
  return el.offsetHeight + detail;
}

/**
 * La FENÊTRE rendue : du premier au dernier index dont la hauteur cumulée
 * recoupe `[defilement, defilement + fenetre]`, élargie de `marge` éléments de
 * part et d'autre, avec les hauteurs des espaces vides avant et après.
 * (C'est la règle que `@tanstack/react-virtual` applique ; elle est écrite ici
 * pour être REJOUÉE avec des rangées dépliables, le composant délègue au
 * virtualiseur.)
 */
export function plageVirtuelle(
  hauteurs: readonly number[],
  defilement: number,
  fenetre: number,
  marge = 5,
): { debut: number; fin: number; hautAvant: number; hautApres: number; total: number } {
  const n = hauteurs.length;
  if (n === 0) return { debut: 0, fin: -1, hautAvant: 0, hautApres: 0, total: 0 };
  const cumul: number[] = new Array(n + 1);
  cumul[0] = 0;
  for (let i = 0; i < n; i += 1) cumul[i + 1] = cumul[i]! + hauteurs[i]!;
  const total = cumul[n]!;
  let debut = 0;
  while (debut < n - 1 && cumul[debut + 1]! <= defilement) debut += 1;
  let fin = debut;
  while (fin < n - 1 && cumul[fin + 1]! < defilement + fenetre) fin += 1;
  debut = Math.max(0, debut - marge);
  fin = Math.min(n - 1, fin + marge);
  return { debut, fin, hautAvant: cumul[debut]!, hautApres: total - cumul[fin + 1]!, total };
}

/* ───────────────────────────── Tri et filtres ────────────────────────────── */

export type ValeurCellule = string | number | null | undefined;

/**
 * Le comparateur du tri : les nombres entre eux, le reste en français avec
 * l'ordre numérique (« 9 » avant « 10 »). Les vides vont EN QUEUE quel que
 * soit le sens — c'est au composant de l'assurer (il range les vides à part),
 * ce comparateur ne reçoit que des valeurs renseignées.
 */
export function comparerValeurs(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'fr', { numeric: true });
}

export const estVide = (v: ValeurCellule): boolean => v == null || v === '';

/** L'état de tri d'une LISTE (cartes, lignes) qui n'est pas un `<Tableau>` : même forme que le sien. */
export interface EtatTri { cle: string; sens: 1 | -1 }

/**
 * Le cycle du tri, LE MÊME que celui du tableau : croissant → décroissant →
 * l'ordre par défaut. Une liste de cartes a presque toujours un ordre par
 * défaut (l'échéance, le code) : « revenir au défaut » y remplace « aucun tri ».
 * Si la colonne cliquée EST le défaut, on ne fait qu'alterner le sens.
 */
export function basculerTri(tri: EtatTri | null, cle: string, defaut: EtatTri | null = null): EtatTri | null {
  if (!tri || tri.cle !== cle) return { cle, sens: 1 };
  if (tri.sens === 1) return { cle, sens: -1 };
  if (defaut && defaut.cle === cle) return { cle, sens: 1 };
  return defaut;
}

/**
 * Trie une liste sur la VALEUR BRUTE d'une clé, les vides EN QUEUE quel que
 * soit le sens, de façon STABLE. C'est le tri du tableau, offert aux listes
 * de cartes (L4) pour qu'il n'y ait qu'un moteur de tri dans le module.
 */
export function trierLignes<L>(
  lignes: readonly L[],
  valeurs: Readonly<Record<string, (l: L) => ValeurCellule>>,
  tri: EtatTri | null,
): L[] {
  if (!tri || !valeurs[tri.cle]) return [...lignes];
  const lire = valeurs[tri.cle]!;
  return lignes
    .map((l, i) => ({ l, i, v: lire(l) }))
    .sort((a, b) => {
      const va = estVide(a.v); const vb = estVide(b.v);
      if (va && vb) return a.i - b.i;
      if (va) return 1;
      if (vb) return -1;
      const c = comparerValeurs(a.v as string | number, b.v as string | number) * tri.sens;
      return c || a.i - b.i;
    })
    .map((x) => x.l);
}

/** Sans accent ni casse : « Écouvillon » et « ecouvillon » se trouvent l'un l'autre. */
export function normaliser(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export type FiltreColonne =
  | { type: 'texte'; q: string }
  | { type: 'liste'; valeurs: string[] }
  | { type: 'date'; de?: string; a?: string }
  | { type: 'nombre'; min?: number; max?: number };

/** Un filtre sans contrainte n'en est pas un : il est retiré de l'état. */
export function filtreActif(f: FiltreColonne | undefined): boolean {
  if (!f) return false;
  switch (f.type) {
    case 'texte': return f.q.trim() !== '';
    case 'liste': return f.valeurs.length > 0;
    case 'date': return !!f.de || !!f.a;
    case 'nombre': return f.min !== undefined || f.max !== undefined;
  }
}

/** La valeur BRUTE d'une cellule passe-t-elle le filtre ? */
export function passeFiltre(v: ValeurCellule, f: FiltreColonne): boolean {
  if (!filtreActif(f)) return true;
  switch (f.type) {
    case 'texte':
      return !estVide(v) && normaliser(String(v)).includes(normaliser(f.q));
    case 'liste':
      return f.valeurs.includes(estVide(v) ? '' : String(v));
    case 'date': {
      if (estVide(v)) return false;
      const j = String(v).slice(0, 10);
      if (f.de && j < f.de) return false;
      if (f.a && j > f.a) return false;
      return true;
    }
    case 'nombre': {
      const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.'));
      if (!Number.isFinite(n)) return false;
      if (f.min !== undefined && n < f.min) return false;
      if (f.max !== undefined && n > f.max) return false;
      return true;
    }
  }
}

/** Une ligne passe-t-elle TOUS les filtres de colonne et la recherche globale ? */
export function passeFiltres<L>(
  ligne: L,
  valeurs: Readonly<Record<string, (l: L) => ValeurCellule>>,
  filtres: Readonly<Record<string, FiltreColonne>>,
  recherche = '',
): boolean {
  for (const [cle, f] of Object.entries(filtres)) {
    const lire = valeurs[cle];
    if (!lire) continue; // colonne disparue : le filtre est inerte, jamais bloquant
    if (!passeFiltre(lire(ligne), f)) return false;
  }
  const q = normaliser(recherche);
  if (!q) return true;
  return Object.values(valeurs).some((lire) => {
    const v = lire(ligne);
    return !estVide(v) && normaliser(String(v)).includes(q);
  });
}

/** Le filtre, dit en clair pour l'en-tête d'export et la puce : « Gravité : critique, majeure ». */
export function decrireFiltre(
  libelle: string, f: FiltreColonne, fmtDate: (iso: string) => string = (s) => s,
  /** Le libellé d'une valeur de liste (un rang de gravité → « critique ») ; la valeur brute sinon. */
  libelleValeur: (v: string) => string = (v) => v,
): string {
  switch (f.type) {
    case 'texte': return `${libelle} contient « ${f.q.trim()} »`;
    case 'liste': return `${libelle} : ${f.valeurs.map((v) => (v === '' ? '(vide)' : libelleValeur(v))).join(', ')}`;
    case 'date':
      if (f.de && f.a) return `${libelle} du ${fmtDate(f.de)} au ${fmtDate(f.a)}`;
      if (f.de) return `${libelle} à partir du ${fmtDate(f.de)}`;
      return `${libelle} jusqu'au ${fmtDate(f.a!)}`;
    case 'nombre':
      if (f.min !== undefined && f.max !== undefined) return `${libelle} entre ${f.min} et ${f.max}`;
      if (f.min !== undefined) return `${libelle} ≥ ${f.min}`;
      return `${libelle} ≤ ${f.max}`;
  }
}

/** Les valeurs distinctes d'une colonne (filtre « liste »), triées, le vide en queue. */
export function valeursDistinctes<L>(lignes: readonly L[], lire: (l: L) => ValeurCellule, max = 200): string[] {
  const vues = new Set<string>();
  let vide = false;
  for (const l of lignes) {
    const v = lire(l);
    if (estVide(v)) { vide = true; continue; }
    vues.add(String(v));
    if (vues.size > max) break;
  }
  const out = [...vues].sort((a, b) => comparerValeurs(a, b));
  if (vide) out.push('');
  return out;
}

/* ───────────────────────────── Actions par lot ───────────────────────────── */

/**
 * Une action applicable à PLUSIEURS lignes.
 *
 * ⚠️ `nature` n'admet PAS « supprimer » : la suppression par lot n'existe pas
 * (PROMPT-TABLEAUX §1). Archiver et retirer sont réversibles ; une erreur de
 * sélection s'y répare. Une suppression de douze lignes d'un geste ne se
 * répare pas.
 */
export interface ActionLot<L> {
  cle: string;
  /** À l'infinitif, c'est le verbe de la confirmation : « Archiver ». */
  libelle: string;
  nature: 'archiver' | 'retirer' | 'autre';
  /** Ce que l'on compte, au pluriel : « documents ». */
  quoi: string;
  /** Traite UNE ligne ; l'appelant y écrit son entrée de journal. Rejette en cas d'échec.
   *  Le `motif` est celui saisi dans la barre quand l'action en exige un (`motif`), sinon ''. */
  executer: (ligne: L, motif: string) => Promise<void>;
  /** Désactive l'action pour certaines lignes (déjà archivée…). */
  applicable?: (ligne: L) => boolean;
  /**
   * L10 (11/10/2026) : l'action EXIGE un motif, commun au lot et journalisé
   * ligne par ligne (« sens corrigé d'après la place du passage… »). La barre
   * de confirmation montre le champ, pré-rempli de `defaut`, et refuse de
   * lancer sous `min` caractères (10 par défaut — la règle des vérifications
   * de pointage).
   */
  motif?: { libelle: string; defaut?: string; min?: number };
  /** Appelé UNE fois le lot terminé, avec le bilan : recharger la liste, poser un message. */
  apresLot?: (bilan: BilanLot) => void | Promise<void>;
}

/** Le motif d'un lot est-il recevable ? Sans `motif` déclaré, toujours. */
export function motifLotValide<L>(action: Pick<ActionLot<L>, 'motif'>, motif: string): boolean {
  if (!action.motif) return true;
  return motif.trim().length >= (action.motif.min ?? 10);
}

export interface BilanLot {
  reussies: number;
  echecs: { cle: string; erreur: string }[];
}

/** « Archiver 12 documents ? » — le nombre est dans la question, toujours. */
export function libelleConfirmationLot(verbe: string, n: number, quoi: string): string {
  const singulier = quoi.endsWith('s') ? quoi.slice(0, -1) : quoi;
  return `${verbe} ${n} ${n > 1 ? quoi : singulier} ?`;
}

/** Refuse ce que le type ne peut pas refuser à l'exécution : un libellé de suppression. */
export function estSuppression(libelle: string): boolean {
  return /supprim|effac|détruire|detruire/i.test(libelle);
}

/**
 * Exécute l'action ligne PAR ligne — jamais un appel groupé — et rend le
 * bilan « x réussies / y en échec » avec la liste des échecs. Un échec n'arrête
 * pas les suivantes : la personne voit ce qui est passé et ce qui ne l'est pas.
 *
 * `journal`, s'il est donné, reçoit UNE entrée par ligne traitée (réussie ou
 * non) : c'est la trace que le test vérifie, et celle qu'un appelant peut
 * brancher sur le journal de l'application.
 */
export async function executerParLot<L>(
  lignes: readonly L[],
  action: ActionLot<L>,
  cleLigne: (l: L) => string,
  journal?: (entree: { cle: string; action: string; ok: boolean; erreur?: string; motif?: string }) => void,
  motif = '',
): Promise<BilanLot> {
  if (estSuppression(action.libelle) || (action.nature as string) === 'supprimer') {
    throw new Error('La suppression par lot n’existe pas : supprimer se fait ligne par ligne, avec confirmation.');
  }
  if (!motifLotValide(action, motif)) {
    throw new Error(`Un motif d’au moins ${action.motif?.min ?? 10} caractères est exigé pour « ${action.libelle} ».`);
  }
  const bilan: BilanLot = { reussies: 0, echecs: [] };
  const m = motif.trim();
  for (const l of lignes) {
    if (action.applicable && !action.applicable(l)) continue;
    const cle = cleLigne(l);
    try {
      await action.executer(l, m);
      bilan.reussies += 1;
      journal?.({ cle, action: action.cle, ok: true, ...(action.motif ? { motif: m } : {}) });
    } catch (e) {
      const erreur = e instanceof Error ? e.message : String(e);
      bilan.echecs.push({ cle, erreur });
      journal?.({ cle, action: action.cle, ok: false, erreur, ...(action.motif ? { motif: m } : {}) });
    }
  }
  return bilan;
}

/** « 10 réussies / 2 en échec » — ou « 12 réussies » quand tout est passé. */
export function libelleBilan(b: BilanLot): string {
  const r = `${b.reussies} réussie${b.reussies > 1 ? 's' : ''}`;
  return b.echecs.length ? `${r} / ${b.echecs.length} en échec` : r;
}

/* ───────────────────────────── Export ────────────────────────────────────── */

/** Les colonnes déclarées `export: true`, dans l'ordre du CODE — jamais celui de l'écran. */
export function colonnesExportables<C extends { export?: boolean }>(colonnes: readonly C[]): C[] {
  return colonnes.filter((c) => c.export === true);
}

/* ───────────────────────────── Stockage local ────────────────────────────── */

/** Clé du cache local des préférences d'un tableau (L1 la migre en base). */
export const cleStockage = (id: string): string => `bio24.tableau.${id}`;
/** Les deux clés HISTORIQUES de `TableauTriable`, encore lues pour ne perdre aucun réglage. */
export const cleOrdreHistorique = (id: string): string => `bio24.colonnes.${id}`;
export const cleLargeursHistorique = (id: string): string => `bio24.largeurs.${id}`;
